import { REACT_APP_API_URL } from "../../../env";

export const REDIS_LOGS_ENDPOINT = "/api/dashboard/admin/redis-logs";
export const MAX_STREAM_ROWS = 1000;
export const MAX_FETCH_LIMIT = 500;

export const DEFAULT_FILTERS = {
  bookingRef: "",
  event: "",
  service: "",
  outcome: "",
  bookingStatus: "",
  lockStatus: "",
  limit: "100",
};

export const FILTER_FIELDS = [
  { name: "bookingRef", label: "Booking ref", placeholder: "012016XXXXXX" },
  { name: "event", label: "Event", placeholder: "hotel_booking_succeeded" },
  { name: "outcome", label: "Outcome", placeholder: "success / failed" },
  { name: "bookingStatus", label: "Booking status", placeholder: "BOOKED" },
  { name: "lockStatus", label: "Lock status", placeholder: "released" },
  { name: "service", label: "Service", placeholder: "booking-service" },
];

export const buildApiUrl = (path) =>
  `${REACT_APP_API_URL.replace(/\/$/, "")}${path.startsWith("/") ? path : `/${path}`}`;

export const extractArray = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.logs)) return payload.logs;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.records)) return payload.records;
  if (Array.isArray(payload?.rows)) return payload.rows;
  if (typeof payload?.logs === "string") return payload.logs.split(/\r?\n/).filter(Boolean);
  if (typeof payload?.data === "string") return payload.data.split(/\r?\n/).filter(Boolean);

  return [];
};

export const parseStreamPayload = (payload) => {
  if (!payload) return [];

  try {
    const parsed = JSON.parse(payload);
    const entries = extractArray(parsed);
    return entries.length ? entries : [parsed];
  } catch (error) {
    return [payload];
  }
};

export const getPayloadMessage = (payload) =>
  payload?.message || payload?.msg || payload?.statusMessage || "";

export const getStatusPayload = (payload) =>
  payload?.status && typeof payload.status === "object" ? payload.status : payload;

export const getRunningState = (payload, fallback) => {
  const status = getStatusPayload(payload);

  if (typeof status?.running === "boolean") return status.running;
  if (typeof status?.isRunning === "boolean") return status.isRunning;
  if (typeof status?.active === "boolean") return status.active;
  if (typeof status?.streaming === "boolean") return status.streaming;
  if (typeof status?.status === "string") {
    return ["running", "started", "active", "streaming"].includes(
      status.status.toLowerCase()
    );
  }

  return fallback;
};

export const getConnectionState = (payload, fallback) => {
  const status = getStatusPayload(payload);
  return typeof status?.connected === "boolean" ? status.connected : fallback;
};

export const getBufferSize = (payload, fallback) => {
  const status = getStatusPayload(payload);
  return Number.isFinite(Number(status?.bookingBufferSize))
    ? Number(status.bookingBufferSize)
    : fallback;
};

export const getMonitorStatusText = (payload, fallback) => {
  const status = getStatusPayload(payload);
  return typeof status?.status === "string" ? status.status : fallback;
};

export const safeStringify = (value) => {
  try {
    return JSON.stringify(value);
  } catch (error) {
    return String(value);
  }
};

export const extractTimeFromText = (text) => {
  const match = text.match(
    /\d{4}-\d{2}-\d{2}[T\s]\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:?\d{2})?/
  );

  return match?.[0] || "";
};

export const extractLevelFromText = (text) => {
  const match = text.match(/\b(ERROR|ERR|WARNING|WARN|INFO|DEBUG|TRACE|SUCCESS)\b/i);
  if (!match) return "LOG";

  return match[1].toUpperCase() === "ERR" ? "ERROR" : match[1].toUpperCase();
};

export const cleanMessageText = (text, timestamp, level) => {
  let message = text;
  if (timestamp) message = message.replace(timestamp, "");
  if (level) message = message.replace(new RegExp(`\\b${level}\\b`, "i"), "");

  return message.replace(/^[\s:[\]-]+/, "").trim() || text;
};

export const getEntryValue = (entry, keys) => {
  for (const key of keys) {
    if (entry?.[key] !== undefined && entry?.[key] !== null && entry?.[key] !== "") {
      return entry[key];
    }
  }

  return "";
};

export const normalizeLogEntry = (entry, index, fallbackTimestamp = "") => {
  if (typeof entry === "string") {
    const timestamp = extractTimeFromText(entry) || fallbackTimestamp;
    const level = extractLevelFromText(entry);

    return {
      id: `redis-log-${index}-${timestamp || entry.slice(0, 20)}`,
      timestamp,
      level,
      service: "Redis",
      summary: cleanMessageText(entry, timestamp, level),
      raw: entry,
    };
  }

  const timestamp =
    getEntryValue(entry, ["timestamp", "time", "createdAt", "created_at", "date"]) ||
    fallbackTimestamp;
  const message = getEntryValue(entry, [
    "summary",
    "message",
    "msg",
    "log",
    "error",
    "details",
  ]);
  const id = getEntryValue(entry, ["id", "_id", "logId"]);

  return {
    id: id !== "" ? id : `redis-log-${index}-${timestamp}`,
    timestamp,
    event: getEntryValue(entry, ["event", "eventName"]),
    level: String(getEntryValue(entry, ["level", "severity"]) || "LOG").toUpperCase(),
    traceId: getEntryValue(entry, ["traceId", "trace_id"]),
    service:
      getEntryValue(entry, ["service", "source", "context", "module"]) || "Redis",
    bookingRef: getEntryValue(entry, ["bookingRef", "booking_ref", "bookingRefNo"]),
    orderId: getEntryValue(entry, ["orderId", "order_id"]),
    paymentId: getEntryValue(entry, ["paymentId", "payment_id"]),
    bookingStatus: getEntryValue(entry, ["bookingStatus", "booking_status"]),
    outcome: getEntryValue(entry, ["outcome"]),
    lockType: getEntryValue(entry, ["lockType", "lock_type"]),
    lockStatus: getEntryValue(entry, ["lockStatus", "lock_status"]),
    deletedKeys: getEntryValue(entry, ["deletedKeys", "deleted_keys"]),
    summary:
      (typeof message === "string" ? message : message && safeStringify(message)) ||
      safeStringify(entry),
    raw: entry,
  };
};

export const formatDateTime = (value) => {
  if (!value) return "-";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  });
};

export const getLevelBadgeClass = (level) => {
  const normalized = String(level || "").toLowerCase();

  if (normalized.includes("error")) return "bg-danger";
  if (normalized.includes("warn")) return "bg-warning text-dark";
  if (normalized.includes("success")) return "bg-success";
  if (normalized.includes("debug") || normalized.includes("trace")) return "bg-secondary";
  if (normalized.includes("info")) return "bg-info text-dark";

  return "bg-light text-dark border";
};

export const getOutcomeBadgeClass = (outcome) => {
  const normalized = String(outcome || "").toLowerCase();
  if (["success", "succeeded", "completed"].includes(normalized)) return "bg-success";
  if (["failed", "failure", "error"].includes(normalized)) return "bg-danger";
  if (["pending", "processing"].includes(normalized)) return "bg-warning text-dark";
  return "bg-secondary";
};

export const sanitizeFilters = (filters, includeLimit = true) => {
  const params = {};

  Object.keys(DEFAULT_FILTERS).forEach((key) => {
    if (key === "limit") return;
    const value = String(filters?.[key] ?? "").trim();
    if (value) params[key] = value;
  });

  if (includeLimit) {
    const requestedLimit = Number(filters?.limit);
    if (Number.isFinite(requestedLimit) && requestedLimit > 0) {
      params.limit = Math.min(MAX_FETCH_LIMIT, Math.floor(requestedLimit));
    }
  }

  return params;
};

export const getErrorMessage = (error, fallback) =>
  error.response?.data?.message || error.response?.data?.error || fallback;
